'use client';

import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { UrlObject } from 'url';
import isPropValid from '@emotion/is-prop-valid';
import { RouterLink } from '../link/RouterLink';
import { ArrowForwardIcon, Css, ITheme } from '../../../ui';
import { Color } from '../../../styles/Color';

interface ITextAndButtonProps {
	label: string;
	href: string | UrlObject;
	hideLabel?: boolean;
	disabled?: boolean;
	icon?: typeof ArrowForwardIcon;
	textPosition?: 'left' | 'right';
}

export default function TextAndButton({
	label,
	href,
	hideLabel,
	icon,
	disabled = false,
	textPosition = 'left',
}: ITextAndButtonProps) {
	const Icon = icon ?? ArrowForwardIcon;
	return (
		<TextAndButtonWrap
			href={href}
			disabled={disabled}
			textPosition={textPosition}
		>
			{!hideLabel && <TextAndButtonLabel>{label}</TextAndButtonLabel>}
			<IconWrapper>
				<Icon
					aria-label={hideLabel ? undefined : label}
					role={hideLabel ? undefined : 'presentation'}
				/>
			</IconWrapper>
		</TextAndButtonWrap>
	);
}

const IconWrapper = styled.div<{}, ITheme>`
	border-radius: ${(props) => props.theme.button?.radius};
	padding: ${Css.pxToRem(8)};
	background: ${(props) => props.theme.button?.primary?.background};
	color: ${(props) => props.theme.button?.primary?.font?.color};
	transition: background 0.2s ease;

	svg path {
		fill: currentColor;
	}
`;

const TextAndButtonWrap = styled(RouterLink, {
	shouldForwardProp: (props) => isPropValid(props),
})<{ disabled: boolean; textPosition?: 'left' | 'right' }, ITheme>`
	display: flex;
	${({ textPosition }) =>
		textPosition === 'right' && `flex-direction: row-reverse;`}
	gap: ${Css.pxToRem(8)};
	align-items: center;
	text-decoration: none;

	${({ disabled }) => `pointer-events: ${disabled ? 'none' : 'auto'};`}

	${IconWrapper} {
		${({ disabled }) =>
			disabled &&
			css`
				pointer-events: none;
				background-color: ${Color.DisabledBackground};
				color: ${Color.Neutral6};
			`}
	}

	&:hover ${IconWrapper} {
		background: ${(props) => props.theme.button?.primary?.hover?.background};
		color: ${(props) => props.theme.button?.primary?.hover?.font?.color};
	}
	&:focus ${IconWrapper} {
		background: ${(props) => props.theme.button?.primary?.focus?.background};
		color: ${(props) => props.theme.button?.primary?.focus?.font?.color};
	}
`;

const TextAndButtonLabel = styled.small<{}, ITheme>`
	font-weight: 700;
	letter-spacing: ${Css.pxToRem(0.84)};
	cursor: pointer;
	color: ${Color.OnBackground};
	font-size: ${Css.pxToRem(16)};
`;
